import type { MegaComponentColumn, Navigation, PlayerListQueryConfig } from '@repo/types';
import { MatchPeriodFilter, MegaComponentType } from '@repo/types';
import API from '@data/endpoints';

/**
 * Shared query descriptors for the header mega-menu component columns.
 *
 * The header renders on every page, so the SSR entry (apps/web/src/
 * entry-server.tsx) prefetches each component column's data on every render.
 * React Query only hydrates when the server uses the exact query key the
 * client hook builds, so the embeds and the SSR prefetch both consume the
 * constants and builders in this file — they must never drift apart.
 */

/** Query config behind the compact "Top scorers" embed (part of its query key). */
export const TOP_SCORERS_QUERY: PlayerListQueryConfig = {
  filters: { season: 'current' },
  stats: [{ id: 'goals', label: 'Goals', source: 'goals', aggregation: 'count', sortable: true }],
  defaultSort: { statId: 'goals', order: 'desc' },
  limit: 4,
};

/** Match query params behind the compact "Fixtures" embed. */
export const NAV_MEGA_FIXTURES_PARAMS = { status: 'fixture', sort: 'asc', limit: 3, page: 1 } as const;

/** Match query params behind the compact "Results" embed. */
export const NAV_MEGA_RESULTS_PARAMS = { status: 'result', sort: 'desc', limit: 3, page: 1 } as const;

// Stale times mirroring the client hooks (useLeagueTableQuery /
// usePlayerListQuery cache for 5 minutes; matches follow the SSR match TTL).
const STALE_TIME_TABLE_MS = 5 * 60 * 1000;
const STALE_TIME_MATCHES_MS = 60 * 1000;
const STALE_TIME_SCORERS_MS = 5 * 60 * 1000;

export type HeaderMegaPrefetch = {
  queryKey: readonly unknown[];
  request:
    | { method: 'get'; url: string; params?: Record<string, unknown> }
    | { method: 'post'; url: string; body: Record<string, unknown> };
  staleTime: number;
};

/**
 * Builds the `/matches` params exactly as FixtureList.tsx does during SSR —
 * teamId/limit/sort/status may be absent from the form data (undefined) and
 * competitionId falls back to '' (not undefined). Also reused by the CMS page
 * component prefetch in entry-server.
 */
export function buildFixtureListParams(compData: Record<string, unknown>): Record<string, unknown> {
  return {
    competition: compData.competitionId ?? '',
    teamId: compData.teamId ?? '',
    period: compData.period,
    periodLimit: compData.periodLimit,
    startDate: compData.period === MatchPeriodFilter.Custom && compData.from ? compData.from : '',
    endDate: compData.period === MatchPeriodFilter.Custom && compData.to ? compData.to : '',
    limit: compData.limit,
    sort: compData.sort,
    status: compData.status,
  };
}

function leagueTablePrefetch(column: MegaComponentColumn, apiUrl: string): HeaderMegaPrefetch | null {
  let competitionId: string | undefined;
  if (column.config) {
    // Mirrors LeagueTable.tsx: selectedLeagueTableId defaults to '11', and the
    // query is disabled when it is explicitly empty.
    const dataConfig = (column.config.dataConfig ?? {}) as { selectedLeagueTableId?: string };
    const selectedLeagueTableId = dataConfig.selectedLeagueTableId ?? '11';
    if (selectedLeagueTableId.length === 0) {
      return null;
    }
    competitionId = selectedLeagueTableId;
  } else {
    competitionId = column.competitionId;
  }
  return {
    queryKey: ['league-table', { competitionId }, apiUrl],
    request: { method: 'get', url: `${apiUrl}${API.endpoints.leagueTable}`, params: { competitionId } },
    staleTime: STALE_TIME_TABLE_MS,
  };
}

function matchesPrefetch(column: MegaComponentColumn, apiUrl: string): HeaderMegaPrefetch {
  let params: Record<string, unknown>;
  if (column.config) {
    // Configured columns render the real FixtureList component.
    params = buildFixtureListParams(column.config);
  } else {
    const embedParams =
      column.component === MegaComponentType.Fixtures ? NAV_MEGA_FIXTURES_PARAMS : NAV_MEGA_RESULTS_PARAMS;
    params = { ...embedParams, competition: column.competitionId };
  }
  return {
    queryKey: ['matches', params, apiUrl],
    request: { method: 'get', url: `${apiUrl}${API.endpoints.matches}`, params },
    staleTime: STALE_TIME_MATCHES_MS,
  };
}

function topScorersPrefetch(column: MegaComponentColumn, apiUrl: string): HeaderMegaPrefetch | null {
  if (column.config) {
    // A configured column renders the full PlayersStatsTable, whose query is
    // assembled inside the component (query-builder + linked presets). That
    // logic is not replicated here — the component fetches on the client.
    return null;
  }
  const params = { query: TOP_SCORERS_QUERY, competition: column.competitionId };
  // Mirrors fetchPlayerListStats in @data/footballPlayers/usePlayerListQuery —
  // axios drops undefined body fields, so team/season/absent competition are
  // simply omitted, exactly as the client sends them.
  const body: Record<string, unknown> = { query: TOP_SCORERS_QUERY, page: 1 };
  if (column.competitionId) {
    body.competition = column.competitionId;
  }
  return {
    queryKey: ['playerListStats', params, apiUrl],
    request: {
      method: 'post',
      url: `${apiUrl}${API.endpoints.footballPlayersStatsQuery}`,
      body,
    },
    staleTime: STALE_TIME_SCORERS_MS,
  };
}

function columnPrefetch(column: MegaComponentColumn, apiUrl: string): HeaderMegaPrefetch | null {
  switch (column.component) {
    case MegaComponentType.LeagueTable:
      return leagueTablePrefetch(column, apiUrl);
    case MegaComponentType.Fixtures:
    case MegaComponentType.Results:
      return matchesPrefetch(column, apiUrl);
    case MegaComponentType.TopScorers:
      return topScorersPrefetch(column, apiUrl);
    default:
      // Mega documents live in a Mixed Mongo field — unknown names are skipped.
      return null;
  }
}

/**
 * Collects the prefetch descriptors for every component column in the header
 * desktop navigation (only top-level desktop nodes can carry a mega config).
 */
export function buildHeaderMegaPrefetches(navigation: Navigation | undefined, apiUrl: string): HeaderMegaPrefetch[] {
  const columns = (navigation?.header?.desktopNodes ?? [])
    .flatMap((node) => node.mega?.columns ?? [])
    .filter((column): column is MegaComponentColumn => column.type === 'component');

  return columns
    .map((column) => columnPrefetch(column, apiUrl))
    .filter((prefetch): prefetch is HeaderMegaPrefetch => prefetch !== null);
}
